import { z } from "zod";
import type { CaseType } from "./case-type";
import { NAME_MAX } from "./string-limits";

export enum CompanyTierEnum {
  BETA = "BETA",
  TRIAL = "TRIAL",
  INDIVIDUAL = "INDIVIDUAL",
  TEAM = "TEAM",
  ENTERPRISE = "ENTERPRISE",
}

/** Default annual discount rate; fall back to this whenever `company.discountRate` is missing. */
export const DEFAULT_DISCOUNT_RATE = 0.08;

/** Geometry + default font size of a single placeholder, in inches/points. */
export interface PlaceholderGeometry {
  /** Placeholder bounding box on its layout (inches). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Default text size from the layout's `<a:defRPr sz>`, in points — PowerPoint will not recompute autofit on open. */
  fontSizePt: number;
}

/** Layout analysis results computed when a PPTX template is uploaded. */
export interface TemplateInfo {
  /** 1-based index of the title slide layout (slideLayoutN.xml) */
  titleLayoutIndex: number;
  /** 1-based index of the content slide layout */
  contentLayoutIndex: number;
  /** Free rectangle (inches) on the content layout where we can place content */
  contentArea: { x: number; y: number; w: number; h: number };
  /** Theme accent colors (hex without #) */
  accentColors: { accent1: string; accent2?: string; accent3?: string };
  /** Placeholder type to use for the title on the title layout (e.g. "ctrTitle" or "title") */
  titlePlaceholder: string;
  /** Placeholder type for subtitle on the title layout (e.g. "subTitle" or "body"), or undefined if none */
  subtitlePlaceholder?: string;
  /** idx attribute of the subtitle placeholder in the layout XML */
  subtitlePlaceholderIdx?: number;
  /** Geometry of the title and subtitle roles, so the builder can bake the right autofit fontScale into long titles. */
  placeholders?: {
    title?: PlaceholderGeometry;
    subtitle?: PlaceholderGeometry;
  };
}

export const companySchema = z.object({
  name: z.string().max(NAME_MAX),
});

export type Company = z.infer<typeof companySchema> & {
  id: string;
  //purchases: add later
  adminUser: string;
  expiryDate: string;
  createdDate: string;
  lastMCIndex: number;
  tier: CompanyTierEnum;
  lastColorIndex: number;
  maxUserCount: number | null;
  /** Company-wide annual discount rate (0..1) for every NPV/KPI; defaults to DEFAULT_DISCOUNT_RATE when missing. */
  discountRate?: number;
  /** The company's case types, each with its standard and statuses; absent = DEFAULT_CASE_TYPES (read through caseTypesOf). */
  caseTypes?: CaseType[];
  /** Cached PPTX template layout analysis - set on template upload */
  templateInfo?: TemplateInfo;
  /** Whether MFA is required for all users in this company */
  mfaRequired?: boolean;
  /** Domains Bayescase may send mail to on this company's behalf (invites, actuals asks); absent or empty = unrestricted. */
  allowedEmailDomains?: string[];
  /** Whether the subscription auto-renews at the end of the term (default: false) */
  subscriptionAutoRenews?: boolean;
  /** Subscription billing term */
  subscriptionTerm?: "MONTH" | "YEAR";
  /** Billing model. "CREDIT" disables the seat gate and consumes credits per case and per active project-month. */
  pricingMode?: "SEAT" | "CREDIT";
  /** Credits consumed when a business case is created (credit mode; default 5). */
  creditsPerCase?: number;
  /** Credits consumed per active project per month (credit mode; default 1). */
  creditsPerProjectMonth?: number;
  /** Company opted out of anonymized model training. Absent = opted in. */
  aiTrainingOptOut?: boolean;
};
